import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import TopBar from "@/components/TopBar";
import LevelUpModal from "@/components/LevelUpModal";
import { BRANCH_META } from "@/lib/branches";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
    ArrowLeft,
    Plus,
    GitCommit,
    MessageSquare,
    Eye,
    FileText,
    Users,
    HelpCircle,
    ListChecks,
    Radio,
    X,
    Trash2,
} from "lucide-react";

const KIND_META = {
    task: { icon: ListChecks, color: "#FF2052", label: "Task" },
    question: { icon: HelpCircle, color: "#6F00FF", label: "Question" },
    note: { icon: FileText, color: "#FFC107", label: "Note" },
    debate: { icon: MessageSquare, color: "#3EB489", label: "Debate" },
    review: { icon: Eye, color: "#0EA5E9", label: "Review" },
};

const LEDGER_ICON = {
    commit: GitCommit,
    debate: MessageSquare,
    review: Eye,
    join: Users,
    note: FileText,
    microtask: ListChecks,
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Workshop() {
    const { slug } = useParams();
    const [discipline, setDiscipline] = useState(null);
    const [me, setMe] = useState(null);
    const [canvas, setCanvas] = useState([]);
    const [ledger, setLedger] = useState([]);
    const [reward, setReward] = useState(null);
    const [open, setOpen] = useState(false);
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [kind, setKind] = useState("note");
    const [live, setLive] = useState(false);
    const [pulseId, setPulseId] = useState(null);

    // Drag state
    const dragRef = useRef({ id: null, dx: 0, dy: 0 });
    const canvasRef = useRef(null);

    const load = () => {
        api.get(`/atlas/discipline/${slug}`).then((r) => setDiscipline(r.data));
        api.get(`/workshop/${slug}/canvas`).then((r) => setCanvas(r.data));
        api.get(`/workshop/${slug}/ledger?limit=40`).then((r) => setLedger(r.data));
        api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
    };

    useEffect(load, [slug]);

    // SSE stream — real-time canvas + ledger updates
    useEffect(() => {
        if (!slug) return;
        const url = `${BACKEND_URL}/api/workshop/${slug}/stream`;
        const es = new EventSource(url, { withCredentials: true });
        es.onopen = () => setLive(true);
        es.onerror = () => setLive(false);
        es.addEventListener("canvas_item", (e) => {
            try {
                const p = JSON.parse(e.data);
                setCanvas((prev) => {
                    if (prev.some((c) => c.id === p.item.id)) return prev;
                    return [p.item, ...prev];
                });
                setPulseId(p.item.id);
                setTimeout(() => setPulseId(null), 1400);
            } catch {}
        });
        es.addEventListener("canvas_update", (e) => {
            try {
                const p = JSON.parse(e.data);
                setCanvas((prev) => prev.map((c) => (c.id === p.item.id ? { ...c, ...p.item } : c)));
            } catch {}
        });
        es.addEventListener("ledger_event", (e) => {
            try {
                const p = JSON.parse(e.data);
                setLedger((prev) => [p.event, ...prev.filter((x) => x.created_at !== p.event.created_at || x.message !== p.event.message)].slice(0, 40));
            } catch {}
        });
        return () => es.close();
    }, [slug]);

    const meta = discipline ? BRANCH_META[discipline.branch] : BRANCH_META.sciences;

    const submitItem = async (e) => {
        e.preventDefault();
        if (!title.trim()) return;
        const km = KIND_META[kind];
        const rect = canvasRef.current?.getBoundingClientRect();
        const w = rect ? rect.width : 800;
        const h = rect ? rect.height : 600;
        const x = Math.random() * Math.max(50, w - 260);
        const y = Math.random() * Math.max(50, h - 180);
        const { data } = await api.post("/workshop/canvas", {
            discipline_slug: slug,
            title,
            body,
            color: km.color,
            kind,
            x,
            y,
        });
        setCanvas((c) => [data.item, ...c.filter((x) => x.id !== data.item.id)]);
        setReward(data.reward);
        setTitle("");
        setBody("");
        setKind("note");
        setOpen(false);
    };

    const onPointerDown = (evt, item) => {
        if (evt.target.tagName === "BUTTON" || evt.target.tagName === "A") return;
        const rect = canvasRef.current.getBoundingClientRect();
        dragRef.current = {
            id: item.id,
            dx: evt.clientX - (rect.left + (item.x || 0)),
            dy: evt.clientY - (rect.top + (item.y || 0)),
        };
        evt.currentTarget.setPointerCapture(evt.pointerId);
    };

    const onPointerMove = (evt) => {
        const d = dragRef.current;
        if (!d.id) return;
        const rect = canvasRef.current.getBoundingClientRect();
        const nx = Math.max(0, Math.min(rect.width - 220, evt.clientX - rect.left - d.dx));
        const ny = Math.max(0, Math.min(rect.height - 120, evt.clientY - rect.top - d.dy));
        setCanvas((prev) => prev.map((c) => (c.id === d.id ? { ...c, x: nx, y: ny } : c)));
    };

    const onPointerUp = async () => {
        const d = dragRef.current;
        if (!d.id) return;
        const item = canvas.find((c) => c.id === d.id);
        dragRef.current = { id: null, dx: 0, dy: 0 };
        if (item) {
            try {
                await api.patch(`/workshop/canvas/${item.id}`, { x: item.x, y: item.y });
            } catch {}
        }
    };

    if (!discipline)
        return (
            <div className="min-h-screen flex items-center justify-center text-slate-500 font-display">
                Loading workshop…
            </div>
        );

    return (
        <div className="min-h-screen w-full pt-24 pb-8 px-4 sm:px-8">
            <TopBar />

            <div className="max-w-7xl mx-auto">
                <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
                    <div>
                        <Link
                            to={`/discipline/${slug}`}
                            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900"
                            data-testid="back-to-discipline"
                        >
                            <ArrowLeft className="w-4 h-4" /> {discipline.name} hub
                        </Link>
                        <h1
                            className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-slate-900 mt-1"
                            data-testid="workshop-title"
                        >
                            {discipline.name} · Workshop
                        </h1>
                        <p className="text-slate-600 text-sm mt-0.5 flex items-center gap-2">
                            The Live Ledger — everything in progress, in the open.
                            <span
                                className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${live ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}
                                data-testid="workshop-live-indicator"
                            >
                                <Radio className={`w-3 h-3 ${live ? "animate-pulse" : ""}`} />
                                {live ? "Live" : "Reconnecting"}
                            </span>
                        </p>
                    </div>
                    <button
                        onClick={() => setOpen(true)}
                        className="arcade-btn"
                        data-variant={discipline.branch}
                        data-testid="add-canvas-item"
                    >
                        <Plus className="w-4 h-4" /> Add to canvas
                    </button>
                </div>

                <div className="grid md:grid-cols-12 gap-4 h-[calc(100vh-16rem)] min-h-[560px]">
                    {/* Canvas (draggable sticky notes) */}
                    <div
                        ref={canvasRef}
                        className="md:col-span-8 crystal rounded-3xl p-5 overflow-hidden relative"
                        style={{ background: `linear-gradient(180deg, #fff 0%, ${meta.soft}50 100%)` }}
                        data-testid="workshop-canvas"
                        onPointerMove={onPointerMove}
                        onPointerUp={onPointerUp}
                        onPointerLeave={onPointerUp}
                    >
                        <div className="absolute top-4 left-5 text-xs uppercase tracking-widest font-bold text-slate-400 z-10 pointer-events-none">
                            Collaborative canvas · drag to arrange
                        </div>
                        {canvas.length === 0 && (
                            <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm pointer-events-none">
                                No stickies yet. Be first to drop one.
                            </div>
                        )}
                        {canvas.map((c) => {
                            const km = KIND_META[c.kind] || KIND_META.note;
                            const Icon = km.icon;
                            const isPulsing = pulseId === c.id;
                            const isMine = me && c.author_id === me.id;
                            return (
                                <motion.div
                                    key={c.id}
                                    onPointerDown={(evt) => onPointerDown(evt, c)}
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className={`absolute w-[220px] rounded-2xl p-3 shadow-lg cursor-grab active:cursor-grabbing select-none ${isPulsing ? "ring-4 ring-humanities/50" : ""}`}
                                    style={{
                                        left: c.x || 20,
                                        top: c.y || 60,
                                        background: c.color || km.color,
                                        color: (c.color || km.color)?.toLowerCase() === "#ffc107" ? "#0F172A" : "#fff",
                                        touchAction: "none",
                                    }}
                                    data-testid={`canvas-item-${c.id}`}
                                >
                                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold opacity-90">
                                        <Icon className="w-3 h-3" />
                                        {km.label}
                                        <span className="ml-auto opacity-70">{c.author_name}</span>
                                    </div>
                                    <div className="font-display font-bold text-base mt-1 leading-tight">{c.title}</div>
                                    {c.body && (
                                        <p className="text-xs mt-1 opacity-90 line-clamp-4">{c.body}</p>
                                    )}
                                    <div className="text-[9px] mt-2 font-mono opacity-70">
                                        {new Date(c.created_at).toLocaleString(undefined, {
                                            month: "short",
                                            day: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                        })}
                                        {isMine && <span className="ml-1.5 uppercase font-bold">· yours</span>}
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>

                    {/* Ledger */}
                    <div
                        className="md:col-span-4 crystal rounded-3xl p-5 overflow-auto"
                        data-testid="workshop-ledger"
                    >
                        <div className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-3 flex items-center gap-2">
                            Activity ledger
                            <span className={`inline-block w-1.5 h-1.5 rounded-full ${live ? "bg-green-500 animate-pulse" : "bg-slate-300"}`} />
                        </div>
                        <div className="space-y-3">
                            {ledger.length === 0 && (
                                <p className="text-sm text-slate-500">No activity yet — drop the first canvas card to seed the ledger.</p>
                            )}
                            <AnimatePresence initial={false}>
                                {ledger.map((e, i) => {
                                    const Icon = LEDGER_ICON[e.event_type] || FileText;
                                    return (
                                        <motion.div
                                            key={`${e.created_at}-${i}`}
                                            initial={{ opacity: 0, x: 8 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0 }}
                                            className="flex gap-2 text-sm"
                                            data-testid={`ledger-event-${i}`}
                                        >
                                            <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" color={meta.color} />
                                            <div className="flex-1 min-w-0">
                                                <div className="ledger-row text-slate-800">
                                                    <span className="font-bold uppercase text-[10px] mr-2" style={{ color: meta.color }}>
                                                        {e.event_type}
                                                    </span>
                                                    {e.message}
                                                </div>
                                                <div className="text-[10px] text-slate-400 font-mono mt-0.5">
                                                    {e.actor_name} · {new Date(e.created_at).toLocaleString()}
                                                </div>
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
                        onClick={() => setOpen(false)}
                    >
                        <motion.form
                            onClick={(e) => e.stopPropagation()}
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            onSubmit={submitItem}
                            className="crystal rounded-3xl p-6 w-full max-w-md relative"
                        >
                            <button type="button" onClick={() => setOpen(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-900" data-testid="canvas-modal-close">
                                <X className="w-5 h-5" />
                            </button>
                            <h2 className="font-display font-black text-2xl tracking-tighter text-slate-900">
                                Drop a workshop card
                            </h2>
                            <p className="text-sm text-slate-500 mt-1">
                                Ideas, tasks, questions — anything the roster should see.
                            </p>
                            <div className="mt-4 flex flex-wrap gap-1.5">
                                {Object.entries(KIND_META).map(([k, m]) => {
                                    const Icon = m.icon;
                                    const active = kind === k;
                                    return (
                                        <button
                                            type="button"
                                            key={k}
                                            onClick={() => setKind(k)}
                                            className={`inline-flex items-center gap-1 rounded-full text-xs font-display font-bold transition-all ${active ? "" : "opacity-60 hover:opacity-100"}`}
                                            style={{
                                                background: active ? m.color : "#F1F5F9",
                                                color: active ? (m.color.toLowerCase() === "#ffc107" ? "#0F172A" : "#fff") : "#334155",
                                                padding: "0.35rem 0.75rem",
                                            }}
                                            data-testid={`kind-${k}`}
                                        >
                                            <Icon className="w-3 h-3" /> {m.label}
                                        </button>
                                    );
                                })}
                            </div>
                            <input
                                autoFocus
                                className="field-input mt-3"
                                placeholder="Title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                required
                                data-testid="canvas-item-title"
                            />
                            <textarea
                                className="field-input mt-3 min-h-[90px]"
                                placeholder="Optional detail…"
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                                data-testid="canvas-item-body"
                            />
                            <div className="mt-4 flex justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={() => setOpen(false)}
                                    className="arcade-btn"
                                    data-variant="ghost"
                                >
                                    Cancel
                                </button>
                                <button
                                    className="arcade-btn"
                                    data-variant={discipline.branch}
                                    data-testid="submit-canvas-item"
                                >
                                    Post to canvas
                                </button>
                            </div>
                        </motion.form>
                    </motion.div>
                )}
            </AnimatePresence>

            <LevelUpModal reward={reward} onClose={() => setReward(null)} />
        </div>
    );
}
